import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { EditorPage } from './pages/EditorPage';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { AuthLayout } from './pages/AuthLayout';
import { TermsOfService, PrivacyPolicy } from './pages/AuthLegal';
import { Install } from './pages/Install';
import { AdminLayout, Dashboard, Users, UserDetail, Settings, Projects, Analytics, ActivityLog, AIProviders, AIProviderDetail, AIPrompts, StorageProviders, VectorPackages, PaymentProviders, CryptoWallets, Disclaimers, SocialProviders } from './pages/admin';
import { ProtectedRoute } from './components/ProtectedRoute';
import { CookieBanner } from './components/CookieBanner';
import './App.css';

function InstallRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/install') return;

    fetch('/api/install/status')
      .then(async (r) => {
        const contentType = r.headers.get('content-type') ?? '';
        if (!contentType.toLowerCase().includes('application/json')) {
          return null;
        }
        return (await r.json()) as { ready?: boolean; needsInstall?: boolean };
      })
      .then((data: { ready?: boolean; needsInstall?: boolean } | null) => {
        if (!data) return;
        if (data.needsInstall && !data.ready) {
          navigate('/install', { replace: true });
        }
      })
      .catch(() => {});
  }, [navigate, location.pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <InstallRedirect />
      <CookieBanner />
      <Routes>
        <Route path="/install" element={<Install />} />
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Route>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <EditorPage />
            </ProtectedRoute>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin', 'system']}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="storage-providers" element={<StorageProviders />} />
          <Route path="library" element={<VectorPackages />} />
          <Route path="vector-packages" element={<Navigate to="/admin/library" replace />} />
          <Route path="ai-providers" element={<AIProviders />} />
          <Route path="ai-providers/new" element={<AIProviderDetail />} />
          <Route path="ai-providers/:providerId" element={<AIProviderDetail />} />
          <Route path="ai-prompts" element={<AIPrompts />} />
          <Route path="payment-providers" element={<PaymentProviders />} />
          <Route path="crypto-wallets" element={<CryptoWallets />} />
          <Route path="disclaimers" element={<Disclaimers />} />
          <Route path="social-providers" element={<SocialProviders />} />
          <Route path="users" element={<Users />} />
          <Route path="users/:userId" element={<UserDetail />} />
          <Route path="projects" element={<Projects />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="activity" element={<ActivityLog />} />
          <Route path="settings" element={<Settings />} />
          <Route path="install" element={<Install embedded />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
